
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function PlanetaCorazonPage() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    setDownloading(true);
    setTimeout(() => {
      setDownloading(false);
      alert('El PDF se descargará próximamente. Estamos preparando el contenido.');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#94A5B7]/20 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/mi-espacio" 
              className="flex items-center gap-2 text-gray-600 hover:text-[#94A5B7] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="text-sm font-medium">Volver a Mi espacio</span>
            </Link>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#94A5B7]/10 rounded-full">
              <i className="ri-gift-line text-[#94A5B7] text-sm"></i>
              <span className="text-xs font-medium text-[#94A5B7]">Ritual Gratuito</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#94A5B7] via-[#7A8FA5] to-[#5C7085] py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-40 h-40 border border-white/30 rounded-full"></div>
          <div className="absolute bottom-5 left-20 w-28 h-28 border border-white/40 rounded-full"></div>
          <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-white/10 rounded-full"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <i className="ri-planet-line text-3xl text-white"></i>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
              <i className="ri-time-line text-white text-sm"></i>
              <span className="text-sm text-white">20 minutos</span>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-white mb-4">
            ¿De qué planeta es tu corazón?
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Un viaje interior para descubrir qué te mueve realmente y reconectar con tus deseos más profundos
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left Column - Content */}
          <div className="lg:col-span-3 space-y-10">
            {/* Sobre este ritual */}
            <section>
              <h2 className="text-2xl font-serif text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-[#94A5B7]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-sparkling-line text-[#94A5B7]"></i>
                </span>
                Sobre este ritual
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Tu corazón tiene su propio lenguaje, su propia forma de latir ante lo que realmente importa. Este ritual te invita a explorar ese territorio íntimo donde habitan tus verdaderos deseos.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  A través de metáforas y preguntas que van más allá de lo racional, descubrirás qué paisajes internos te llaman, qué emociones te guían y qué sueños has dejado de lado sin darte cuenta.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Este es un espacio seguro para ser honesta contigo misma, sin juicios ni expectativas. Solo tú y tu corazón, explorando juntos.
                </p>
              </div>
            </section>

            {/* Qué necesitas */}
            <section>
              <h2 className="text-2xl font-serif text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-[#94A5B7]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-[#94A5B7]"></i>
                </span>
                Qué necesitas
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-time-line', text: '20 minutos sin prisas' },
                  { icon: 'ri-pencil-line', text: 'Diario o cuaderno' },
                  { icon: 'ri-moon-line', text: 'Un momento de calma' },
                  { icon: 'ri-emotion-line', text: 'Disposición a sentir' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#94A5B7]/15">
                    <div className="w-10 h-10 bg-[#94A5B7]/15 rounded-lg flex items-center justify-center">
                      <i className={`${item.icon} text-[#94A5B7] text-lg`}></i>
                    </div>
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Pasos del ritual */}
            <section>
              <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-[#94A5B7]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-route-line text-[#94A5B7]"></i>
                </span>
                Pasos del ritual
              </h2>
              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Crea tu atmósfera',
                    description: 'Elige un momento del día donde puedas estar tranquila. Apaga notificaciones y date permiso para desconectar del mundo exterior.'
                  },
                  {
                    step: 2,
                    title: 'Conecta con tu respiración',
                    description: 'Antes de empezar, haz 5 respiraciones profundas. Siente cómo tu cuerpo se relaja y tu mente se aquieta.'
                  },
                  {
                    step: 3,
                    title: 'Viaja con las preguntas',
                    description: 'El PDF te guiará a través de un viaje imaginario. Deja que las imágenes y sensaciones fluyan sin forzar nada.'
                  },
                  {
                    step: 4,
                    title: 'Escribe lo que descubras',
                    description: 'Al terminar, escribe todo lo que vino a ti. No importa si tiene sentido o no, solo déjalo fluir al papel.'
                  }
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#94A5B7] to-[#7A8FA5] rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {item.step}
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Download Card */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-[#F3F6F9] to-[#94A5B7]/15 rounded-2xl p-6 border border-[#94A5B7]/20 shadow-lg">
                {/* PDF Icon */}
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-md flex items-center justify-center mb-4">
                    <i className="ri-file-pdf-2-line text-4xl text-[#94A5B7]"></i>
                  </div>
                  <h3 className="text-xl font-serif text-gray-800 mb-1">Guía del Ritual</h3>
                  <p className="text-sm text-gray-500">Descarga tu PDF gratuito</p>
                </div>

                {/* What's included */}
                <div className="space-y-3 mb-6">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Incluye:</p>
                  {[
                    'Meditación guiada escrita',
                    'Mapa de tu paisaje interior',
                    '5 preguntas de exploración',
                    'Ejercicio de visualización',
                    'Carta a tu corazón'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <i className="ri-check-line text-[#A8B88A]"></i>
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Download Button */}
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full py-4 bg-gradient-to-r from-[#94A5B7] to-[#7A8FA5] text-white rounded-xl font-medium hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-70"
                >
                  {downloading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Preparando descarga...
                    </>
                  ) : (
                    <>
                      <i className="ri-download-2-line text-lg"></i>
                      Descargar PDF Gratis
                    </>
                  )}
                </button>

                {/* File info */}
                <p className="text-center text-xs text-gray-400 mt-3">
                  PDF • 3.1 MB • 10 páginas
                </p>
              </div>

              {/* Additional info */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-[#E8E4E0]">
                <div className="flex items-start gap-3">
                  <i className="ri-moon-line text-[#94A5B7] text-lg mt-0.5"></i>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Este ritual es especialmente poderoso al atardecer o antes de dormir, cuando la mente está más receptiva.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Other Rituals */}
      <section className="bg-[#F5F2EF] py-12 mt-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-serif text-gray-800 mb-6">Otros rituales gratuitos</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/rituales/musa-interior"
              className="bg-white rounded-xl p-5 border border-[#F7CDCE]/20 hover:shadow-lg transition-all duration-300 flex items-center gap-4 cursor-pointer group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#F7CDCE] to-[#BEA6BE] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <i className="ri-magic-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-1">La Musa que tienes dentro</h3>
                <p className="text-sm text-gray-500">15 min • Conecta con tu inspiración</p>
              </div>
              <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#F7CDCE] transition-colors"></i>
            </Link>
            <Link 
              to="/rituales/fuente-ideas"
              className="bg-white rounded-xl p-5 border border-[#A7A86C]/20 hover:shadow-lg transition-all duration-300 flex items-center gap-4 cursor-pointer group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#A7A86C] to-[#D6D57B] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <i className="ri-lightbulb-flash-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-1">La fuente de las ideas</h3>
                <p className="text-sm text-gray-500">18 min • Conecta con tu voz creativa</p>
              </div>
              <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#A7A86C] transition-colors"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F2EF] border-t border-[#E8E4E0] py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500">
            © 2025 Una voz más que propia. Todos los derechos reservados.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-xs text-gray-400 hover:text-[#C4A5C7] transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
